import { type AddMemberResponse, type PlatformOrganizationDetail, STAFF_ROLES } from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Field, Input, Select } from '@dnx/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, ShieldCheck, ShieldOff, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { roleLabel } from '../lib/labels.js';
import { fmtDate } from './queries.js';

function AddMemberForm({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ email: '', name: '', role: 'org_admin' });
  const [result, setResult] = useState<(AddMemberResponse & { email: string }) | null>(null);
  const [copied, setCopied] = useState(false);
  const add = useMutation({
    mutationFn: () => api<AddMemberResponse>(`/platform/organizations/${orgId}/members`, { method: 'POST', body: form }),
    onSuccess: async (res) => {
      setResult({ ...res, email: form.email });
      setForm({ email: '', name: '', role: form.role });
      await qc.invalidateQueries({ queryKey: ['platform', 'orgs'] });
    },
  });

  return (
    <Card>
      <CardHeader title="Adicionar pessoa" description="Se o e-mail já tiver conta, só adicionamos o papel nesta clínica." />
      <form className="grid gap-4 p-5 sm:grid-cols-[1fr_1fr_180px_auto] sm:items-end"
        onSubmit={(e) => { e.preventDefault(); setResult(null); add.mutate(); }}>
        <Field label="Nome"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dra. Ana Souza" /></Field>
        <Field label="E-mail"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ana@clinica.com.br" /></Field>
        <Field label="Papel">
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {STAFF_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </Select>
        </Field>
        <Button type="submit" loading={add.isPending} disabled={form.name.trim().length < 2 || !form.email.includes('@')}>
          <UserPlus className="size-4" />Adicionar
        </Button>
      </form>
      <div className="px-5 pb-5 empty:hidden">
        {add.error && <Alert tone="danger">{add.error.message}</Alert>}
        {result?.temporaryPassword && (
          <Alert tone="success" title={`Conta criada para ${result.email}`}>
            <p>Senha temporária — <strong className="text-fg">exibida só agora</strong>. Envie por um canal seguro.
              No primeiro acesso a pessoa cadastra o aplicativo autenticador (QR code).</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="rounded-md border border-border bg-surface px-2.5 py-1.5 font-mono text-sm tracking-wide text-fg">{result.temporaryPassword}</code>
              <Button variant="secondary" size="sm" onClick={() => { void navigator.clipboard.writeText(result.temporaryPassword!); setCopied(true); }}>
                {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}{copied ? 'Copiada' : 'Copiar'}
              </Button>
            </div>
          </Alert>
        )}
        {result && !result.temporaryPassword && <Alert tone="success">Papel adicionado a {result.email}, que já tinha conta.</Alert>}
      </div>
    </Card>
  );
}

function ResetMfaButton({ orgId, userId, name }: { orgId: string; userId: string; name: string }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const reset = useMutation({
    mutationFn: () => api<void>(`/platform/organizations/${orgId}/members/${userId}/reset-mfa`, { method: 'POST' }),
    onSuccess: async () => { setConfirming(false); await qc.invalidateQueries({ queryKey: ['platform', 'orgs', orgId] }); },
  });
  if (!confirming) {
    return <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}><KeyRound className="size-4" />Redefinir 2 etapas</Button>;
  }
  return (
    <span className="flex items-center justify-end gap-1.5">
      <span className="text-xs text-fg-muted">Desconectar {name.split(' ')[0]} e pedir novo QR?</span>
      <Button variant="danger" size="sm" loading={reset.isPending} onClick={() => reset.mutate()}>Redefinir</Button>
      <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>Cancelar</Button>
    </span>
  );
}

export function TeamTab({ org }: { org: PlatformOrganizationDetail }) {
  return (
    <div className="grid gap-5">
      <AddMemberForm orgId={org.id} />
      <Card className="overflow-hidden">
        <CardHeader title="Equipe" description={`${org.members.length} ${org.members.length === 1 ? 'pessoa' : 'pessoas'} com acesso ao painel desta clínica.`} />
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-y border-border bg-surface-muted/60 text-left text-[12px] text-fg-muted">
              <tr>
                <th className="px-5 py-2.5 font-medium">Pessoa</th>
                <th className="px-5 py-2.5 font-medium">Papéis</th>
                <th className="px-5 py-2.5 font-medium">Duas etapas</th>
                <th className="px-5 py-2.5 font-medium">Último acesso</th>
                <th className="px-5 py-2.5"><span className="sr-only">Ações</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {org.members.map((m) => (
                <tr key={m.userId}>
                  <td className="px-5 py-3"><span className="block font-medium text-fg">{m.name}</span><span className="block text-xs text-fg-muted">{m.email}</span></td>
                  <td className="px-5 py-3 text-fg-muted">{m.roles.map(roleLabel).join(', ')}</td>
                  <td className="px-5 py-3">
                    {m.mfaEnrolled
                      ? <Badge tone="success"><ShieldCheck className="size-3" aria-hidden />Ativa</Badge>
                      : <Badge tone="warning"><ShieldOff className="size-3" aria-hidden />Aguardando 1º acesso</Badge>}
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap text-fg-muted">{fmtDate(m.lastLoginAt, true)}</td>
                  <td className="px-5 py-3 text-right">{m.mfaEnrolled && <ResetMfaButton orgId={org.id} userId={m.userId} name={m.name} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {org.members.length === 0 && <p className="px-5 py-10 text-center text-sm text-fg-muted">Ninguém ainda. Adicione o administrador da clínica acima.</p>}
        </div>
      </Card>
    </div>
  );
}
